import { useLabelVersion } from '../../../../features/labels/LabelsProvider';
import { labelId } from '../../../../features/labels/labels';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  BarElement,
  ArcElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { alignProperty } from '@mui/material/styles/cssUtils';

// --- Registro base de Chart.js ---
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  BarElement,
  ArcElement
);

/* ----------------------------------------------------
   🎨 Colores y helpers
---------------------------------------------------- */
const COLORS = {
  red: '#F87171',
  blue: '#60A5FA',
  yellow: '#FBBF24',
  green: '#4ADE80',
  gray: '#94A3B8',
  delivered_food:"#7dc4e8cc",
  sfr_value:"rgb(232, 94, 48)"  ,
  alarms_count:"rgb(255, 204, 102)",
  perdidos_count:"rgb(255, 102, 102)",
  visitas_count:"rgb(102, 255, 178)",
  alarms_by_ton:"rgb(205, 126, 206)",
  alarms_by_visits:"rgb(205, 126, 206)",
};

const formatNumber = (num) => {
  if (num == null || isNaN(num)) return '-';
  const decimals = (num % 1 === 0) ? 0 : (num.toString().split('.')[1]?.length || 0);
  const precision = Math.min(decimals, 2);
  return num.toLocaleString('es-CL', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

const MONTHS_ES = {
  enero: 0,
  febrero: 1,
  marzo: 2,
  abril: 3,
  mayo: 4,
  junio: 5,
  julio: 6,
  agosto: 7,
  septiembre: 8,
  octubre: 9,
  noviembre: 10,
  diciembre: 11,
};

const parseTemporalLabel = (label) => {
  const normalized = String(label ?? '').trim();

  const dayMatch = normalized.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (dayMatch) {
    const [, day, month, year] = dayMatch;
    return {
      kind: 'day',
      sortValue: new Date(Number(year), Number(month) - 1, Number(day)).getTime(),
    };
  }

  const weekMatch = normalized.match(/^Semana\s+(\d{1,2})(?:\s+(\d{4}))?$/i);
  if (weekMatch) {
    const [, week, year] = weekMatch;
    return {
      kind: 'week',
      sortValue: year ? Number(year) * 100 + Number(week) : Number(week),
    };
  }

  const monthMatch = normalized.match(/^([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s+(\d{4})$/);
  if (monthMatch) {
    const [, monthNameRaw, year] = monthMatch;
    const monthIndex = MONTHS_ES[monthNameRaw.toLowerCase()];
    if (monthIndex !== undefined) {
      return {
        kind: 'month',
        sortValue: new Date(Number(year), monthIndex, 1).getTime(),
      };
    }
  }

  return { kind: 'text', sortValue: Number.NaN };
};

const sortTemporalLabels = (labels) =>
  [...labels].sort((a, b) => {
    const parsedA = parseTemporalLabel(a);
    const parsedB = parseTemporalLabel(b);

    if (!Number.isNaN(parsedA.sortValue) && !Number.isNaN(parsedB.sortValue)) {
      return parsedA.sortValue - parsedB.sortValue;
    }

    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });

const formatTemporalTickLabel = (label) => {
  const normalized = String(label ?? '').trim();
  const parsed = parseTemporalLabel(normalized);

  if (parsed.kind === 'day') {
    const [day, month] = normalized.split('/');
    return `${day}/${month}`;
  }

  if (parsed.kind === 'week') {
    const match = normalized.match(/^Semana\s+(\d{1,2})(?:\s+(\d{4}))?$/i);
    return match ? `Sem ${match[1]}` : normalized;
  }

  if (parsed.kind === 'month') {
    const match = normalized.match(/^([A-Za-zÁÉÍÓÚáéíóúñÑ]+)\s+(\d{4})$/);
    return match ? match[1] : normalized;
  }

  return normalized;
};

const formatTemporalDisplayLabel = (label) => {
  const normalized = String(label ?? '').trim();
  const parsed = parseTemporalLabel(normalized);

  if (parsed.kind === 'day') {
    const [day, month, year] = normalized.split('/');
    return new Date(Number(year), Number(month) - 1, Number(day))
      .toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
      .replace('.', '')
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  return normalized;
};

// Variables globales de sincronización
let syncedTooltipIndex = null;
let syncedTooltipX = null;

const normalizeValuesSummary = (valuesSummary) => {
  if (Array.isArray(valuesSummary)) {
    return valuesSummary
      .filter(Boolean)
      .map((entry) => ({
        day: entry.day,
        value: entry.value || {},
      }))
      .filter((entry) => entry.day);
  }

  if (valuesSummary && typeof valuesSummary === 'object') {
    return Object.entries(valuesSummary).map(([day, value]) => ({
      day,
      value: value || {},
    }));
  }

  return [];
};

const getDisplayNameFromKey = (key, item) => {
  if (item?.name) return item.name;

  const parts = String(key ?? '').split('-').filter(Boolean);
  return parts.at(-1) || String(key ?? '');
};

const normalizeAnalyticSeries = (analyticData, nivelSeleccionado) => {
  const entries = Object.entries(analyticData || {});
  if (!entries.length) return [];

  const firstValue = entries[0]?.[1];
  const shouldFlattenCenters =
    nivelSeleccionado === 'area' ||
    (firstValue &&
      typeof firstValue === 'object' &&
      !Array.isArray(firstValue) &&
      !firstValue.values_summary);

  if (shouldFlattenCenters) {
    return entries.flatMap(([, centersObj]) =>
      Object.entries(centersObj || {})
        .map(([centerKey, centerObj]) => ({
          key: centerKey,
          name: getDisplayNameFromKey(centerKey, centerObj),
          label: labelId(centerKey, "rac", getDisplayNameFromKey(centerKey, centerObj)),
          values_summary: normalizeValuesSummary(centerObj?.values_summary),
        }))
        .filter((serie) => serie.values_summary.length)
    );
  }

  return entries
    .map(([key, item]) => ({
      key,
      name: getDisplayNameFromKey(key, item),
      values_summary: normalizeValuesSummary(item?.values_summary),
    }))
    .filter((serie) => serie.values_summary.length);
};

/* ----------------------------------------------------
    Componente principal
---------------------------------------------------- */
const MenuAnaliticaSimple = ({ data, dias, nivelSeleccionado, translate }) => {
  const labelsRevision = useLabelVersion();
  const t = (key, fallback, params) => {
    const value = typeof translate === 'function' ? translate(key, params) : key;
    return value && value !== key ? value : fallback;
  };
  const analyticData = data?.analytic_data || {};
  const analyticSeries = useMemo(
    () => normalizeAnalyticSeries(analyticData, nivelSeleccionado),
    [analyticData, nivelSeleccionado, labelsRevision] // eslint-disable-line react-hooks/exhaustive-deps -- La versión invalida labels del resolvedor en memoria.
  );

  const [hoveredCage, setHoveredCage] = useState(null);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(null);
  const [activeTooltipDate, setActiveTooltipDate] = useState(null);

  // ---  Estado para vw dinámico ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const vw = (v) => (windowWidth * v) / 100;

  // --- Detección de orientación ---
  const [isPortrait, setIsPortrait] = useState(window.matchMedia('(orientation: portrait)').matches);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(orientation: portrait)');
    const handleOrientationChange = (e) => setIsPortrait(e.matches);
    mediaQuery.addEventListener('change', handleOrientationChange);
    return () => mediaQuery.removeEventListener('change', handleOrientationChange);
  }, []);

  // --- Procesamiento de datos ---
  const jaulasData = useMemo(() => {
    const result = {};
    analyticSeries.forEach((jaula) => {
      const daily = jaula.values_summary.map((d) => ({
        day: d.day,
        ...d.value,
      }));
      const total = daily.reduce(
        (acc, d) => {
          acc.alarmas += d.alarms_count || 0;
          acc.visitas += d.visitas_count || 0;
          acc.perdidos += d.perdidos_count || 0;
          acc.delivered_food += d.delivered_food || 0;
          acc.sfr_sum += d.sfr_value || 0;
          acc.sfr_count += d.sfr_value ? 1 : 0;
          acc.alarms_by_ton += d.alarms_by_ton || 0;
          acc.alarms_by_visits += d.alarms_by_visits || 0;
          return acc;
        },
        {
          alarmas: 0,
          visitas: 0,
          perdidos: 0,
          delivered_food: 0,
          sfr_sum: 0,
          sfr_count: 0,
          alarms_by_ton: 0,
          alarms_by_visits: 0,
        }
      );
      result[jaula.key] = {
        name: jaula.name,
        label: jaula.label,
        daily,
        totals: {
          ...total,
          sfr: total.sfr_count > 0 ? total.sfr_sum / total.sfr_count : 0,
        },
      };
    });
    return result;
  }, [analyticSeries]);

  // --- Totales globales ---
  const resumen = useMemo(() => {
    let alarmas = 0,
      visitas = 0,
      perdidos = 0,
      delivered_food = 0,
      sfrSum = 0,
      sfrCount = 0,
      alarmsByTon = 0,
      alarmsByVisits = 0;
    Object.values(jaulasData).forEach(({ totals }) => {
      alarmas += totals.alarmas;
      visitas += totals.visitas;
      perdidos += totals.perdidos;
      delivered_food += totals.delivered_food;
      sfrSum += totals.sfr_sum;
      sfrCount += totals.sfr_count;
      alarmsByTon += totals.alarms_by_ton;
      alarmsByVisits += totals.alarms_by_visits;
    });
    return {
      alarmas,
      visitas,
      perdidos,
      delivered_food,
      sfr: sfrCount > 0 ? Math.round(sfrSum / sfrCount) : 0,
      byTon: alarmsByTon,
      byVisit: alarmsByVisits,
    };
  }, [jaulasData]);

  // --- Totales diarios ---
  const dailyTotals = useMemo(() => {
    const totals = {};
    Object.values(jaulasData).forEach(({ daily }) => {
      daily.forEach((d) => {
        const day = d.day;
        if (!totals[day])
          totals[day] = { alarmas: 0, visitas: 0, perdidos: 0, sfr_sum: 0, sfr_count: 0, delivered_food: 0, alarms_by_ton: 0, alarms_by_visits: 0  };
        totals[day].alarmas += d.alarms_count || 0;
        totals[day].alarms_by_ton += d.alarms_by_ton || 0;
        totals[day].alarms_by_visits += d.alarms_by_visits || 0;
        totals[day].visitas += d.visitas_count || 0;
        totals[day].perdidos += d.perdidos_count || 0;
        totals[day].delivered_food += d.delivered_food || 0;
        if (d.sfr_value) {
          totals[day].sfr_sum += d.sfr_value;
          totals[day].sfr_count++;
        }
      });
    });
    return totals;
  }, [jaulasData]);

  // const days = Object.keys(dailyTotals).sort(
  //   (a, b) => new Date(a.split('/').reverse().join('-')) - new Date(b.split('/').reverse().join('-'))
  // );


  // const days = Object.keys(dailyTotals)
  // .filter(Boolean)
  // .sort(
  //   (a, b) => new Date(a.split('/').reverse().join('-')) - new Date(b.split('/').reverse().join('-'))
  // );
  
  const days = sortTemporalLabels(Object.keys(dailyTotals).filter(Boolean));
    
  const isSingleDay = days.length === 1;

  // --- Generador de datasets ---
  const makeSparkData = (key, color, calcFn) => {
    const values = days.map((day) => {
      const d = dailyTotals[day];
      if (!d) return 0;
      if (key === 'sfr') return d.sfr_count ? d.sfr_sum / d.sfr_count : 0;
      if (calcFn) return calcFn(d);
      return d[key] || 0;
    });

    const first = values[0] || 0;
    const last = values.at(-1) || 0;
    const change = first === 0 ? 0 : ((last - first) / first) * 100;

    return {
      data: {
        labels: [...days],
        datasets: [
          {
            data: values,
            fill: true,
            borderColor: color,
            backgroundColor: (ctx) => {
              const gradient = ctx.chart.ctx.createLinearGradient(0, 0, 0, ctx.chart.height);

              // 🧠 Detecta el tipo de color
              if (color.startsWith('#') && color.length === 7) {
                // Ej: "#F87171" → agrega transparencia (20%)
                gradient.addColorStop(0, color + '33');
              } else if (color.startsWith('#') && color.length === 9) {
                // Ej: "#F87171cc" → ya tiene alpha → usa directo
                gradient.addColorStop(0, color);
              } else if (color.startsWith('rgba')) {
                // Ej: "rgba(255, 204, 102, 0.8)" → usa directo
                gradient.addColorStop(0, color);
              } else {
                // fallback: color plano
                gradient.addColorStop(0, color);
              }

              gradient.addColorStop(1, 'transparent');
              return gradient;
            },
            tension: 0.4,
            pointRadius: 0,
          },
        ],
      },
      change,
    };
  };



  const barOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item) => `${formatNumber(item.raw, 2)}`,
        },
      },
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: '#CBD5E1',
          // font: { size: 10 },
          font: { size: Math.max(vw(0.7), 12) },
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            return formatTemporalTickLabel(label);
          },
        },
        grid: {
          display: true,
          color: 'rgba(148, 163, 184, 0.2)', // gris suave
        },
      },
      y: {
        ticks: {
          color: '#CBD5E1',
          font: { size: Math.max(vw(0.7), 12) },
          callback: (val) => formatNumber(val),
        },
        grid: { color: '#334155' },
      },
    },
  };

const makeBarDataByCage = (key, color) => {
  const labels = Object.values(jaulasData).map((j) => j.label ?? j.name);

  // 🔄 Mapeo de claves humanas a claves de datos reales
  const fieldMap = {
    alarmas: 'alarms_count',
    perdidos: 'perdidos_count',
    visitas: 'visitas_count',
    delivered_food: 'delivered_food',
    sfr: 'sfr_value',
    alarms_by_ton: 'alarms_by_ton',
    alarms_by_visits: 'alarms_by_visits',
  };

  const realKey = fieldMap[key] || key;

  const values = Object.values(jaulasData).map((j) => {
    const dailyValue = j.daily.find((d) => d.day === days[0]);
    if (!dailyValue) return 0;

    // ⚡ obtener valor según tipo
    if (realKey === 'sfr_value') return dailyValue.sfr_value || 0;
    return dailyValue[realKey] ?? 0;
  });

  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: color,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };
};




  // --- Opciones del gráfico ---
  const chartRefs = useRef([]);

  useEffect(() => {
    const handleMouseLeave = () => {
      setActiveTooltipIndex(null);
      setActiveTooltipDate(null);
    };

    // 🔍 Escucha cuando el mouse sale de cualquiera de los charts
    const canvases = document.querySelectorAll('canvas');
    canvases.forEach((canvas) =>
      canvas.addEventListener('mouseleave', handleMouseLeave)
    );

    return () => {
      canvases.forEach((canvas) =>
        canvas.removeEventListener('mouseleave', handleMouseLeave)
      );
    };
  }, []);

  const createSparkOptions = (color, chartIndex) => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { bottom: vw(1.5) } },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        display: true,
        ticks: {
          color: '#CBD5E1',
          // font: { size: 10 },
          font: { size: Math.max(vw(0.7), 12) },
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            return formatTemporalTickLabel(label);
          },
        },
        grid: {
          display: true,
          color: 'rgba(148, 163, 184, 0.2)', // gris suave
        },
      },    
     // x: {
      //  display: true,
      //   // offset: true,
      //   ticks: {
      //     color: '#CBD5E1',
      //     font: { size: Math.max(vw(0.7), 12) },
      //     maxRotation: 0,
      //     autoSkip: true,
      //     callback: function (value, index, ticks) {
      //       // Chart.js entrega el índice y las etiquetas en context.chart.data.labels
      //       const labels = this.chart?.data?.labels;
      //       if (!labels || !labels[index]) return '';
      //       const label = labels[index];
      //       const date = new Date(label.split('/').reverse().join('-'));
      //       const formatted = date
      //         .toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
      //         .replace('.', '')
      //         .replace(/^\w/, (c) => c.toUpperCase());
      //       return formatted;
      //     },
      //   },
      //   grid: { display: true, drawBorder: true, color: '#334155' },
      //   title: {
      //     display: false, // puedes activarlo si quieres
      //   },
      // },
      y: {
        display: true,
        ticks: {
          color: '#CBD5E1',
          font: { size: Math.max(vw(0.7), 12) },
          callback: (val) => formatNumber(val),
        },
        grid: { color: '#334155', drawBorder: true },
      },
    },
    plugins: {
     legend: { display: false },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        backgroundColor: '#1E293B',
        titleColor: '#E2E8F0',
        bodyColor: '#E2E8F0',
        displayColors: false,
        padding: 10,
        callbacks: {
          title: (items) => items[0].label,
          label: (item) => `${t("value", "Valor")}: ${formatNumber(item.raw, 2)}`,
        },
        external: (context) => {
          const tooltip = context.tooltip;
          console.log('tooltip',tooltip)
          if (tooltip?.dataPoints?.length) {
            syncedTooltipIndex = tooltip.dataPoints[0].dataIndex;
            syncedTooltipX = tooltip.dataPoints[0].element.x;
            setActiveTooltipIndex(syncedTooltipIndex);

            const date = tooltip.dataPoints[0].label;
            setActiveTooltipDate(formatTemporalDisplayLabel(date));

            document.dispatchEvent(
              new CustomEvent('sync-tooltips', { detail: { source: chartIndex } })
            );
          } else {
            console.log('se limpia')
            // limpiar estados cuando el tooltip desaparece
            setActiveTooltipIndex(null);
            setActiveTooltipDate(null);
          }
        },
      },
    },
    elements: {
      line: { borderWidth: vw(0.15) },
      point: { radius: 0, hoverRadius: vw(0.4), hoverBackgroundColor: '#fff' },
    },
  });

     /** --- Crear ranking por métrica --- **/
  const getRanking = (metric) => {
    return Object.values(jaulasData)
      .map(({ name, label, daily, totals }) => {
        const first = daily[0]?.[metric === 'sfr' ? 'sfr_value' : `${metric}_count`] || 0;
        const last = daily[daily.length - 1]?.[metric === 'sfr' ? 'sfr_value' : `${metric}_count`] || 0;
        const change = first === 0 ? 0 : ((last - first) / first) * 100;
        return {
          name, label,
          value: totals[metric] || 0,
          change,
        };
      })
      .sort((a, b) => b.value - a.value);
  };


  const renderKPI = ({
  label,
  value,
  spark,
  metricKey,
  color,
  isPercent = false,
  index,
}) => {
  const sparkOptions = createSparkOptions(color, index);
  const ranking = getRanking(metricKey);

  const isPositive = spark.change >= 0;
  const trendColor = !isPositive ? COLORS.green : COLORS.red;
  const currentValue =
    activeTooltipIndex != null
      ? spark.data.datasets[0].data[activeTooltipIndex]
      : spark.data.datasets[0].data.at(-1);
  const hoveredSeries = hoveredCage
    ? Object.values(jaulasData).find((item) => item.name === hoveredCage)
    : null;
  // console.log('hoveredCage',hoveredCage)
  // console.log('hoveredCage',jaulasData)
  return (
    <Box
      key={label}
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: '1vw',
        backgroundColor: 'rgba(255,255,255,0)',
        border:'solid 0.01vw #e84e0e',
         // background: 'linear-gradient(180deg,#1E293B 0%,#0F172A 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 0 10px rgba(0,0,0,0.2)',
        transition: 'all 0.25s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 0 20px rgba(0,0,0,0.35)',
        },
      }}
    >
      {/* Simula el CardContent */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '1vw',
          // gap: '0.5vw', // mantiene separación consistente entre secciones
        }}
      >
        <Box style={{ display:'grid', gridTemplateColumns:'repeat(2, 50%)'}}>
          <Box >
          {/* --- Título --- */}
          <Typography sx={{ color: color, fontSize: '1vw' }}>
            {label}
          </Typography>

          {/* --- Valor principal --- */}
          <Box display="flex" alignItems="flex-end" gap="0.6vw">
            <Typography sx={{ fontSize: '1.6vw', fontWeight: 'bold' }}>
              {isPercent ? `${value}%` : formatNumber(value, 2)}
            </Typography>
            {!isSingleDay && (
              <Typography
                sx={{
                  color: trendColor,
                  fontSize: '0.9vw',
                  fontWeight: 600,
                  mb: '0.3vh',
                }}
              >
                {isPositive ? '▲' : '▼'} {Math.abs(spark.change).toFixed(2)}%
              </Typography>
            )}
          </Box>

          {/* --- Valor actual --- */}
          {activeTooltipIndex !== null && activeTooltipDate && (
            <Typography
                variant="body2"
                sx={{ color: COLORS.gray, fontSize: '0.9vw' }}
              >
                ({activeTooltipDate}) {formatNumber(currentValue, 2)}
              </Typography>
          )}

          </Box>
          <Box style={{ height: '12vh', display:'flex', justifyContent:'center', alignItems:'center', alignContent:'center'}}>
            {hoveredSeries && (
              <Box
                sx={{
                  width: '100%',
                  height: '12vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // border:'solid 0.01vw red'
                }}
              >
                {/* Título con el nombre de la jaula */}
                <Typography
                  sx={{
                    color: '#E2E8F0',
                    fontSize: '0.9vw',
                    fontWeight: 600,
                    mb: '0.5vh',
                    textAlign: 'center',
                  }}
                >
                  {nivelSeleccionado === 'area'
                    ? t('center_label', 'Centro')
                    : t('cage_label', 'Jaula')} - {hoveredSeries?.label ?? hoveredCage}
                </Typography>

                {/* Mini gráfico */}
                <Box style={{height:'8vh', width:'100%', }}>
                  <Line
                    data={{
                      labels: hoveredSeries.daily.map((d) => d.day),
                      datasets: [
                        {
                          data: hoveredSeries.daily.map((d) => {
                            if (metricKey === 'sfr') return d.sfr_value || 0;
                            if (metricKey === 'delivered_food') return d.delivered_food || 0;
                            if (metricKey === 'alarms_by_ton') return d.alarms_by_ton || 0;
                            if (metricKey === 'alarms_by_visits') return d.alarms_by_visits || 0;
                            if (metricKey === 'alarmas') return d.alarms_count || 0;
                            return d[`${metricKey}_count`] ?? 0;
                          }),
                          borderColor: color,
                          backgroundColor: (context) => {
                          const chart = context.chart;
                          const { ctx, chartArea } = chart;
                          if (!chartArea) return null; // aún no está listo

                          const gradient = ctx.createLinearGradient(0, 0, 0, chartArea.bottom);

                          // Detecta el tipo de color y genera versión con alpha
                          let startColor = color;
                          let endColor = 'transparent';

                          if (color.startsWith('#')) {
                            // Color hexadecimal → convertir a RGBA
                            const hex = color.replace('#', '');
                            const bigint = parseInt(hex, 16);
                            const r = (bigint >> 16) & 255;
                            const g = (bigint >> 8) & 255;
                            const b = bigint & 255;
                            startColor = `rgba(${r}, ${g}, ${b}, 0.5)`; // mitad opaco
                            endColor = `rgba(${r}, ${g}, ${b}, 0)`;     // transparente
                          } else if (color.startsWith('rgb(')) {
                            // Color tipo rgb() → convertir a rgba()
                            const rgbValues = color.match(/\d+/g);
                            startColor = `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, 0.5)`;
                            endColor = `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, 0)`;
                          }

                          gradient.addColorStop(0, startColor);
                          gradient.addColorStop(1, endColor);

                          return gradient;
                        },
                          // backgroundColor: (context) => {
                          //   const chart = context.chart;
                          //   const { ctx, chartArea } = chart;
                          //   if (!chartArea) return; // evita error antes del render inicial
                          //   const gradient = ctx?.createLinearGradient(0, 0, 0, chartArea?.bottom);
                          //   gradient?.addColorStop(0, `${color}80`); // 50% opaco arriba
                          //   gradient?.addColorStop(1, `${color}00`); // transparente abajo
                          //   return gradient;
                          // },
                          tension: 0.4,
                          fill: true,
                          pointRadius: 0,
                        },
                      ],
                    }}
                    // options={sparkOptions}
                    options={{
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: '#1E293B',
        titleColor: '#E2E8F0',
        bodyColor: '#E2E8F0',
        displayColors: false,
        callbacks: {
          title: (items) => items[0].label,
          label: (item) => `${t("value", "Valor")}: ${item.formattedValue}`,
        },
      },
    },
    elements: {
      line: { borderJoinStyle: 'round' },
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: '#CBD5E1',
          font: { size: 10 },
          callback: function (value, index, ticks) {
            const label = this.getLabelForValue(value);
            if (!label) return '';
            return formatTemporalTickLabel(label);
          },
        },
        grid: {
          display: true,
          color: 'rgba(148, 163, 184, 0.2)', // gris suave
        },
      },
      y: {
        display: true,
        ticks: {
          color: '#CBD5E1',
          font: { size: 10 },
          callback: (val) => val.toLocaleString('es-CL'),
        },
        grid: {
          display: true,
          color: 'rgba(148, 163, 184, 0.2)',
        },
      },
    },
  }}
                    // options={{
                    //   responsive: true,
                    //   maintainAspectRatio: false,
                    //   plugins: { legend: { display: false }, tooltip: { enabled: false } },
                    //   elements: {
                    //     line: { borderJoinStyle: 'round' },
                    //   },
                    //   scales: {
                    //     x: { display: false },
                    //     y: {
                    //       display: false,
                    //       beginAtZero: true,
                    //     },
                    //   },
                    // }}
                  />
                </Box>
              </Box>
            )}
          </Box>

        </Box>
        {/* --- Gráfico --- */}
        <Box
          sx={{
            width: '100%',
            height: '14vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop:'0.5vh',
          }}
        >
          {isSingleDay ? (
            <Bar
              data={makeBarDataByCage(metricKey, color)}
              options={barOptions}
              height={'100%'}
            />
          ) : (
            <Line
              ref={(ref) => (chartRefs.current[index] = ref?.chartInstance)}
              data={spark.data}
              options={sparkOptions}
              height={'100%'}
            />
          )}
        </Box>

        {/* --- Ranking --- */}
        <Box sx={{ mt: '-1vh', flexGrow: 1 }}>
          {ranking.slice(0, 3).map((r) => {
            const rPositive = r.change >= 0;
            return (
              <Box
                key={r.name}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ py: '0.2vh' }}
              >
                <Typography sx={{ fontSize: '.9vw' }}>{r.label ?? r.name}</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography sx={{ fontSize: '.9vw' }}>
                    {formatNumber(r.value, 2)}
                  </Typography>
                  {!isSingleDay && (
                    <Typography
                      sx={{
                        fontSize: '0.7vw',
                        color: rPositive ? COLORS.green : COLORS.red,
                        minWidth: '4vw',
                        textAlign: 'right',
                      }}
                    >
                      {rPositive ? '▲' : '▼'} {Math.abs(r.change).toFixed(2)}%
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {ranking.length > 3 && (
          <AutoScrollRanking
            ranking={ranking.slice(3)}
            formatNumber={formatNumber}
            COLORS={COLORS}
            isSingleDay={isSingleDay}
            onHoverCage={setHoveredCage}
            hoveredCage={hoveredCage}

          />
        )}
      </Box>
    </Box>
  );
};


  // --- Render principal ---
  const rangoTexto =
    dias?.startDate && dias?.endDate
      ? `${dias.startDate} → ${dias.endDate}`
      : 'Rango no definido';

  const sparks = [
    makeSparkData('alarmas', COLORS.alarms_count),
    makeSparkData('perdidos', COLORS.perdidos_count),
    makeSparkData('alarms_by_ton', COLORS.alarms_by_ton),
    makeSparkData('alarms_by_visits', COLORS.alarms_by_visits),
    // makeSparkData('alarms_by_ton', COLORS.alarms_by_ton, (d) =>
    //   d.delivered ? d.alarmas / d.delivered : 0
    // ),
    // makeSparkData('alarms_by_visits', COLORS.alarms_by_visits, (d) =>
    //   d.visitas ? d.alarmas / d.visitas : 0
    // ),
    makeSparkData('sfr', COLORS.sfr_value),
    makeSparkData('visitas', COLORS.visitas_count),
    makeSparkData('delivered_food', COLORS.delivered_food),
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        color: '#E2E8F0',
        // padding: '2vh 2vw',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontWeight="bold" sx={{ fontSize: '1.5vw' }}>
          Resumen General
        </Typography>
        <Typography color={COLORS.gray} sx={{ fontSize: '1vw' }}>
          Periodo: {rangoTexto}
        </Typography>
      </Box> */}
      {/* === Fila 1: Alarmas === */}
      <Box
        display="grid"
        gridTemplateColumns={isPortrait ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)'}
        gap="1vw"
        sx={{ width: '100%', mb: '1vh', mt: '1vh', placeItems: 'center' }}
      >
        {renderKPI({
          label: t('alarms', 'Alarmas'),
          value: resumen.alarmas,
          spark: sparks[0],
          metricKey: 'alarmas',
          color: COLORS.alarms_count,
          index: 0,
        })}
        {renderKPI({
          label: t('alarms_per_ton', 'Alarms per Ton'),
          value: resumen.byTon,
          spark: sparks[2],
          metricKey: 'alarms_by_ton',
          color: COLORS.alarms_by_ton,
          index: 2,
        })}
        {renderKPI({
          label: t('alarms_per_visit', 'Alarms per Visit'),
          value: resumen.byVisit,
          spark: sparks[3],
          metricKey: 'alarms_by_visits',
          color: COLORS.alarms_by_visits,
          index: 3,
        })}
      </Box>

      {/* === Fila 2: Otras métricas === */}
      <Box
        display="grid"
        gridTemplateColumns={isPortrait ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)'}
        gap="1vw"
        sx={{ width: '100%', mt: '0vh', placeItems: 'center' }}
      >
        {renderKPI({
          label: t('pellets_lost', 'Lost pellets'),
          value: resumen.perdidos,
          spark: sparks[1],
          metricKey: 'perdidos',
          color: COLORS.perdidos_count,
          index: 1,
        })}
        {renderKPI({
          label: t('average_sfr', 'Average SFR'),
          value: resumen.sfr,
          spark: sparks[4],
          metricKey: 'sfr',
          color: COLORS.sfr_value,
          isPercent: true,
          index: 4,
        })}
        {renderKPI({
          label: t('visits', 'Visits'),
          value: resumen.visitas,
          spark: sparks[5],
          metricKey: 'visitas',
          color: COLORS.visitas_count,
          index: 5,
        })}
        {renderKPI({
          label: t('delivered_food', 'Delivered food'),
          value: resumen.delivered_food,
          spark: makeSparkData('delivered_food', COLORS.delivered_food),
          metricKey: 'delivered_food',
          color: COLORS.delivered_food,
          index: 6,
        })}
      </Box>
 
    </Box>
  );
};

export default MenuAnaliticaSimple;



const AutoScrollRanking = ({ ranking, formatNumber, COLORS, isSingleDay, onHoverCage,hoveredCage  }) => {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [velocity, setVelocity] = useState(0);
  const [lastX, setLastX] = useState(0);
  const [momentumActive, setMomentumActive] = useState(false);

  // === Movimiento automático continuo (siempre activo) ===
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let frameId;
    const speed = 0.8; // velocidad constante del scroll automático

    const tick = () => {
      // Solo se pausa si el usuario está arrastrando o hay momentum
      if (!isDragging && !momentumActive) {
        el.scrollLeft += speed;

        // bucle infinito
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft = 0;
        }
      }
      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [isDragging, momentumActive]);

  // === Drag manual con momentum ===
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setMomentumActive(false);
    setLastX(e.pageX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const el = scrollRef.current;
    const dx = e.pageX - lastX;
    el.scrollLeft -= dx;
    setVelocity(dx);
    setLastX(e.pageX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    applyMomentum();
  };

  const handleMouseLeave = () => {
    // si el mouse sale mientras se arrastra, soltamos y aplicamos momentum
    if (isDragging) {
      setIsDragging(false);
      applyMomentum();
    }
  };

  // === Eventos táctiles (para móvil) ===
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setMomentumActive(false);
    setLastX(e.touches[0].pageX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const el = scrollRef.current;
    const dx = e.touches[0].pageX - lastX;
    el.scrollLeft -= dx;
    setVelocity(dx);
    setLastX(e.touches[0].pageX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    applyMomentum();
  };

  // === Momentum scroll (inercia) ===
  const applyMomentum = () => {
    const el = scrollRef.current;
    let currentVelocity = velocity;
    setMomentumActive(true);

    const step = () => {
      if (Math.abs(currentVelocity) < 0.1) {
        setMomentumActive(false);
        return;
      }
      el.scrollLeft -= currentVelocity;
      currentVelocity *= 0.95; // desaceleración
      if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      else if (el.scrollLeft <= 0) el.scrollLeft = el.scrollWidth / 2;
      requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  return (
    <Box
      ref={scrollRef}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      sx={{
        display: 'flex',
        overflowX: 'hidden',
        gap: '1vw',
        // mt: 1,
        // py: 1,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        whiteSpace: 'nowrap',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        marginLeft:'-1vw',
        minWidth: '120%', // asegura que haya más ancho para scroll
        // border:'solid 0.01vw'
      }}
    >
      {ranking.concat(ranking).map((r, i) => {
        const rPositive = r.change >= 0;
        return (
          <Box
            key={`${r.name}-${i}`}
            onMouseEnter={() => onHoverCage(r.name)}
            onMouseLeave={() => onHoverCage(null)}
            sx={{
              width: '8vw',
              // background: '#1E293B',
              background: hoveredCage === r.name ? '#334155' : '#1E293B',
              borderRadius: '0.6vw',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #334155',
              flexShrink: 0,
              transition: 'transform 0.2s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
          >
            <Typography sx={{ fontSize: '0.9vw', color: '#E2E8F0' }}>
              {r.label ?? r.name}
            </Typography>

            <Box
              sx={{
                minWidth: '12vw',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '0.9vw' }}>
                {formatNumber(r.value, 2)}
              </Typography>
              { !isSingleDay && <Typography
                sx={{
                  fontSize: '0.8vw',
                  color: rPositive ? COLORS.green : COLORS.red,
                }}
              >
                {rPositive ? '▲' : '▼'} {Math.abs(r.change).toFixed(2)}%
              </Typography>}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
